import styled from "styled-components";
import React, { useEffect, useMemo, useRef, useState, type ReactNode, } from "react";
import { gsap, } from "gsap";
import { getDeviceType, getTheme, themed, Box, Panel, Icon, IconButton, } from "@bracket/ui";

import type * as ICONS from "@bracket/ui/icons";



export declare type IconPosition = "top-right" | "bottom-right" | "bottom-left" | "top-left";

export declare type ExpanderProps = {
	children: ReactNode[];
	columns?: number;
	className?: string;
	gap?: string;
	expandText?: string;
	collapseText?: string;
	rotateIcon?: boolean;
	animationDuration?: number;
	icon?: keyof typeof ICONS;
	iconSpacing?: string;
	iconPosition?: IconPosition;
	iconScale?: string | number;
	iconRotation?: number;
	iconCircle?: boolean;
	iconCirclePad?: string;
	iconCircleWidth?: string;
	iconCircleColor?: string;
	iconSize?: string;
	hideBackgroundButton?: boolean;
};



/**
 * Expander component that displays children in a grid layout with expand/collapse functionality
 * @param {object} props - Component props
 * @param {ReactNode[]} props.children - Array of child components to display (up to 20)
 * @param {number=} props.columns - Number of columns in the grid (default: 3)
 * @param {string=} props.className - Additional CSS classes
 * @param {string=} props.gap - Gap between grid items (default: "1rem")
 * @param {string=} props.expandText - Text for expand button (default: "Show more")
 * @param {string=} props.collapseText - Text for collapse button (default: "Show less")
 * @param {boolean=} props.rotateIcon - Whether to rotate icon when expanded (default: true)
 * @param {number=} props.animationDuration - Animation duration in milliseconds (default: 375)
 * @param {keyof typeof import("../Icons").default=} props.icon - Icon to use for expand/collapse button (default: "Chevron")
 * @param {string=} props.iconSpacing - CSS spacing value for icon offset from edges (default: "0")
 * @param {"top-right" | "bottom-right" | "bottom-left" | "top-left"=} props.iconPosition - Position of the icon button (default: "bottom-right")
 * @param {string | number=} props.iconScale - CSS scale value to apply to the icon button (e.g., "1.5" or 1.5)
 * @param {number=} props.iconRotation - Initial rotation angle in degrees (1-360). When expanded, rotates 180 degrees from this value
 * @param {boolean=} props.iconCircle - Whether to draw a circle border around the icon button (passed through to IconButton)
 * @param {string=} props.iconCirclePad - CSS size value for padding between circle border and icon (passed through to IconButton)
 * @param {string=} props.iconCircleWidth - CSS size value for circle border width (passed through to IconButton, default: "1px")
 * @param {string=} props.iconCircleColor - CSS color value for circle border (passed through to IconButton, default: "#fff")
 * @returns {JSX.Element}
 */
export const Expander: React.FC<ExpanderProps> = ({
	children,
	columns = 3,
	className,
	gap = "1rem",
	expandText = "Show more",
	collapseText = "Show less",
	rotateIcon = true,
	animationDuration = 375,
	icon = "Chevron" as keyof typeof ICONS,
	iconSpacing = "0",
	iconPosition = "bottom-right",
	iconScale,
	iconRotation,
	iconCircle,
	iconCirclePad,
	iconCircleWidth,
	iconCircleColor,
	iconSize,
	hideBackgroundButton,
}) => {
	themed();

	const deviceType = getDeviceType();
	const [isExpanded, setIsExpanded] = useState(false);
	const [isAnimating, setIsAnimating] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const gridRef = useRef<HTMLDivElement | null>(null);
	const animationRef = useRef<gsap.core.Tween | undefined>(undefined);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const iconAnimationRef = useRef<gsap.core.Tween | undefined>(undefined);
	
	// Calculate rotation: initial rotation + 180 degrees when expanded (with wrapping)
	const calculatedRotation = useMemo(() => {
		if(iconRotation === undefined) {
			return undefined;
		}
		const baseRotation = Math.max(1, Math.min(360, iconRotation));
		if(isExpanded) {
			const rotated = baseRotation + 180;
			// Wrap around if exceeds 360
			return rotated > 360 ? rotated - 360 : rotated;
		}
		return baseRotation;
	}, [iconRotation, isExpanded]);
	
	// Convert children to array and limit to 20 items - React.Children.toArray already assigns unique keys
	const childArray = useMemo(() => React.Children.toArray(children).slice(0, 20), [children]);
	
	// Flatten Fragment children to count actual grid items
	const flattenChildren = (childrenArray: ReactNode[]): ReactNode[] => {
		const flattened: ReactNode[] = [];
		for(const child of childrenArray) {
			if(React.isValidElement(child) && child.type === React.Fragment) {
				const fragmentChildren: ReactNode = "props" in child && child.props && typeof child.props === "object" && "children" in child.props
					? (child.props as { children?: ReactNode, }).children
					: null;
				if(fragmentChildren) {
					const fragmentArray = React.Children.toArray(fragmentChildren);
					flattened.push(...fragmentArray);
				}
			} else {
				flattened.push(child);
			}
		}
		return flattened;
	};
	
	const flattenedChildren = useMemo(() => flattenChildren(childArray), [childArray]);
	const totalItems = flattenedChildren.length;
	const hasMoreItems = useMemo(() => totalItems > columns, [totalItems, columns]);
	
	// Detect mobile view
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};
		
		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => {
			window.removeEventListener("resize", checkMobile);
		};
	}, []);
	
	// Build a mapping from flattened index back to original child structure for rendering
	const buildRenderItems = (limit: number | null): ReactNode[] => {
		if(limit === null) {
			return childArray;
		}
		const itemsToInclude: ReactNode[] = [];
		let flattenedCount = 0;
		for(const child of childArray) {
			if(React.isValidElement(child) && child.type === React.Fragment) {
				const fragmentChildren: ReactNode = "props" in child && child.props && typeof child.props === "object" && "children" in child.props
					? (child.props as { children?: ReactNode, }).children
					: null;
				if(fragmentChildren) {
					const fragmentArray = React.Children.toArray(fragmentChildren);
					const fragmentLength = fragmentArray.length;
					if(flattenedCount + fragmentLength <= limit) {
						// Include entire Fragment
						itemsToInclude.push(child);
						flattenedCount += fragmentLength;
					} else {
						// Partial Fragment - create new Fragment with only items that fit
						const remainingSlots = limit - flattenedCount;
						if(remainingSlots > 0) {
							// Create a new Fragment with a key if the original had one
							const partialFragment = child.key
								? React.createElement(React.Fragment, { key: child.key, }, ...fragmentArray.slice(0, remainingSlots))
								: React.createElement(React.Fragment, null, ...fragmentArray.slice(0, remainingSlots));
							itemsToInclude.push(partialFragment);
						}
						break;
					}
				} else {
					if(flattenedCount < limit) {
						itemsToInclude.push(child);
						flattenedCount += 1;
					} else {
						break;
					}
				}
			} else {
				if(flattenedCount < limit) {
					itemsToInclude.push(child);
					flattenedCount += 1;
				} else {
					break;
				}
			}
		}
		return itemsToInclude;
	};
	
	// Show 'columns' items initially (first row on desktop, first column on mobile), all when expanded
	const itemsToShow = useMemo(() => buildRenderItems(isExpanded ? null : columns), [isExpanded, childArray, columns]);

	const handleToggle = () => {
		if (isAnimating) return;
		
		// If iconRotation is provided, use CSS rotation instead of GSAP
		const useCssRotation = iconRotation !== undefined;
		
		if (!isExpanded) {
			// Expanding - rotate icon immediately, then update state (point up when expanded)
			const svg = buttonRef.current?.querySelector("svg");
			if (svg && rotateIcon && !useCssRotation) {
				if (iconAnimationRef.current) {
					iconAnimationRef.current.kill();
				}
				iconAnimationRef.current = gsap.to(svg, {
					rotation: 180,
					duration: 0.2,
					ease: "power2.inOut",
				});
			} else if (svg && useCssRotation && iconAnimationRef.current) {
				// Clear any existing GSAP rotation when using CSS rotation
				iconAnimationRef.current.kill();
				iconAnimationRef.current = undefined;
				gsap.set(svg, { rotation: 0, clearProps: "rotation" });
			}
			setIsExpanded(true);
		} else {
			// Collapsing - rotate icon immediately, then animate (point down when collapsed)
			const svg = buttonRef.current?.querySelector("svg");
			if (svg && rotateIcon && !useCssRotation) {
				if (iconAnimationRef.current) {
					iconAnimationRef.current.kill();
				}
				iconAnimationRef.current = gsap.to(svg, {
					rotation: 0,
					duration: 0.2,
					ease: "power2.inOut",
				});
			} else if (svg && useCssRotation && iconAnimationRef.current) {
				// Clear any existing GSAP rotation when using CSS rotation
				iconAnimationRef.current.kill();
				iconAnimationRef.current = undefined;
				gsap.set(svg, { rotation: 0, clearProps: "rotation" });
			}
			setIsAnimating(true);
			
			// Use requestAnimationFrame to ensure DOM is ready
			requestAnimationFrame(() => {
				const grid = gridRef.current;
				if (!grid) {
					setIsExpanded(false);
					setIsAnimating(false);
					return;
				}

				const gridItems = Array.from(grid.children) as HTMLElement[];
				const itemsToHide = gridItems.slice(columns);
				const durationInSeconds = animationDuration / 1000;

				if (animationRef.current) {
					animationRef.current.kill();
				}

				if (itemsToHide.length > 0) {
					// Use fromTo to ensure smooth animation from visible state
					const anim = gsap.fromTo(
						itemsToHide,
						{
							opacity: 1,
							y: 0,
							scale: 1,
						},
						{
							opacity: 0,
							y: -10,
							scale: 0.95,
							duration: durationInSeconds,
							ease: "power2.in",
							stagger: 0.03,
							onComplete: () => {
								setIsExpanded(false);
								setIsAnimating(false);
								animationRef.current = undefined;
								// Reset transform properties after animation
								gsap.set(itemsToHide, { clearProps: "all" });
							},
						},
					);
					animationRef.current = anim;
				} else {
					setIsExpanded(false);
					setIsAnimating(false);
				}
			});
		}
	};

	useEffect(() => {
		const grid = gridRef.current;
		if (!grid || !isExpanded || isAnimating) return;

		// Kill any existing animation
		if (animationRef.current) {
			animationRef.current.kill();
			animationRef.current = undefined;
		}

		const gridItems = Array.from(grid.children) as HTMLElement[];
		if (gridItems.length === 0) return;

		const durationInSeconds = animationDuration / 1000;

		// Use requestAnimationFrame to ensure DOM is updated before animating
		requestAnimationFrame(() => {
			// When expanding, animate only the newly visible items (those beyond the initial columns)
			const visibleItems = gridItems.slice(columns);
			if (visibleItems.length > 0) {
				gsap.fromTo(
					visibleItems,
					{
						opacity: 0,
						y: -10,
						scale: 0.95,
					},
					{
						opacity: 1,
						y: 0,
						scale: 1,
						duration: durationInSeconds,
						ease: "power2.out",
						stagger: 0.03,
					},
				);
			}
		});

		return () => {
			if (animationRef.current) {
				animationRef.current.kill();
				animationRef.current = undefined;
			}
		};
	}, [isExpanded, animationDuration, columns, isAnimating, rotateIcon]);

	// Clear GSAP rotation when iconRotation is provided to avoid conflicts with CSS rotation
	useEffect(() => {
		if(iconRotation !== undefined && buttonRef.current) {
			const svg = buttonRef.current.querySelector("svg");
			if(svg && iconAnimationRef.current) {
				iconAnimationRef.current.kill();
				iconAnimationRef.current = undefined;
				gsap.set(svg, { rotation: 0, clearProps: "rotation" });
			}
		}
	}, [iconRotation]);

	// When collapsing, keep items in DOM until animation completes
	const displayItems = isAnimating ? childArray : itemsToShow;

	// Measure and equalize grid item heights (desktop only)
	useEffect(() => {
		const grid = gridRef.current;
		if(!grid || isAnimating) return;

		const equalizeHeights = () => {
			const items = Array.from(grid.children) as HTMLElement[];
			if(items.length === 0) return;
			// On mobile: reset any fixed heights to natural auto height
			if(isMobile) {
				items.forEach(item => {
					item.style.height = "auto";
				});
				return;
			}

			// Measure all heights
			const heights: number[] = [];
			items.forEach(item => {
				// Temporarily reset height to get natural height
				const originalHeight = item.style.height;
				item.style.height = "auto";
				heights.push(item.offsetHeight);
				item.style.height = originalHeight;
			});

			// Find max height
			const maxHeight = Math.max(...heights);
			if(maxHeight > 0) {
				// Set all items to max height
				items.forEach(item => {
					item.style.height = `${maxHeight}px`;
				});
			}
		};

		// Use requestAnimationFrame to ensure DOM is ready
		requestAnimationFrame(() => {
			equalizeHeights();
		});

		// Re-equalize on window resize
		const handleResize = () => {
			requestAnimationFrame(() => {
				equalizeHeights();
			});
		};

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, [isExpanded, isAnimating, displayItems, isMobile]);

		const isTopPosition = iconPosition === "top-left" || iconPosition === "top-right";
		const isBottomPosition = iconPosition === "bottom-left" || iconPosition === "bottom-right";
		const showButtonAbove = hasMoreItems && isMobile && isTopPosition;
		const showButtonBelow = hasMoreItems && isMobile && isBottomPosition;
		const showButtonOverlay = hasMoreItems && !isMobile;

		return (
		<Container>
			{showButtonAbove && (
				<MobileButtonWrapper key="expand-button-mobile-top">
					<IconButton
						ref={buttonRef}
						icon={icon}
						onClick={handleToggle}
						disabled={isAnimating}
						aria-label={isExpanded ? collapseText : expandText}
						iconScale={iconScale}
						iconRotation={calculatedRotation}
						iconCircle={iconCircle}
						iconCirclePad={iconCirclePad}
						iconCircleWidth={iconCircleWidth}
						iconCircleColor={iconCircleColor}
						button={!hideBackgroundButton}
						color="transparent"
					/>
				</MobileButtonWrapper>
			)}
			<StyledPanel className={className} $hasButton={showButtonOverlay}>
				{showButtonOverlay && (
					<ExpandButtonWrapper key="expand-button" $iconSpacing={iconSpacing} $iconPosition={iconPosition}>
						<IconButton
							ref={buttonRef}
							icon={icon}
							onClick={handleToggle}
							disabled={isAnimating}
							aria-label={isExpanded ? collapseText : expandText}
							iconScale={iconScale}
							iconRotation={calculatedRotation}
							iconCircle={iconCircle}
							iconCirclePad={iconCirclePad}
							iconCircleWidth={iconCircleWidth}
							iconCircleColor={iconCircleColor}
							iconSize={iconSize}
						/>
					</ExpandButtonWrapper>
				)}
				<Grid ref={gridRef} $columns={isMobile ? 1 : columns} $gap={gap} $isMobile={isMobile}>
				{displayItems.map((child, index) => {
					if (React.isValidElement(child)) {
						// If child is a React.Fragment, extract its children and render them inline
						if (child.type === React.Fragment) {
							const fragmentChildren: ReactNode = "props" in child && child.props && typeof child.props === "object" && "children" in child.props
								? (child.props as { children?: ReactNode, }).children
								: null;
							return (
								<React.Fragment key={child.key ?? `fragment-${index}`}>
									{React.Children.map(fragmentChildren, (fragmentChild, fragmentIndex) => {
										if (React.isValidElement(fragmentChild)) {
											return <Box key={fragmentChild.key ?? `fragment-child-${fragmentIndex}`} style={{ flexDirection: "column", ...(isMobile ? { width: "100%", minWidth: 0, overflow: "hidden", } : {}), }}>{fragmentChild}</Box>;
										}
										return fragmentChild;
									})}
								</React.Fragment>
							);
						}
						return <Box key={child.key} style={{ flexDirection: "column", ...(isMobile ? { width: "100%", minWidth: 0, overflow: "hidden", } : {}), }}>{child}</Box>;
					}
					return null;
				})}
				</Grid>
			</StyledPanel>
			{showButtonBelow && (
				<MobileButtonWrapper key="expand-button-mobile-bottom">
					<IconButton
						ref={buttonRef}
						icon={icon}
						onClick={handleToggle}
						disabled={isAnimating}
						aria-label={isExpanded ? collapseText : expandText}
						iconScale={iconScale}
						iconRotation={calculatedRotation}
						iconCircle={iconCircle}
						iconCirclePad={iconCirclePad}
						iconCircleWidth={iconCircleWidth}
						iconCircleColor={iconCircleColor}
						iconSize={iconSize}
					/>
				</MobileButtonWrapper>
			)}
		</Container>
	);
};



const Container = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const Grid = styled.div<{ $columns: number; $gap: string; $isMobile: boolean; }>`
	display: grid;
	grid-template-columns: ${({ $isMobile, $columns, }) => $isMobile ? "1fr" : `repeat(${$columns}, 1fr)`};
	column-gap: ${({ $gap, }) => $gap};
	row-gap: calc(${({ $gap, }) => $gap} * 0.25);
	width: 100%;
	overflow: hidden;
	
	${({ $isMobile, }) => $isMobile ? `
		> * {
			min-width: 0;
			width: 100%;
			overflow: hidden;
		}
	` : ""}
`;

const StyledPanel = styled(Panel)<{ $hasButton: boolean; }>`
	position: relative;
`;

const ExpandButtonWrapper = styled.div<{ $iconSpacing: string; $iconPosition: IconPosition; }>`
	position: absolute;
	z-index: 1;	
	${({ $iconPosition, $iconSpacing, }) => {
		switch ($iconPosition) {
			case "top-right":
				return `
					top: ${$iconSpacing};
					right: ${$iconSpacing};
				`;
			case "bottom-right":
				return `
					bottom: ${$iconSpacing};
					right: ${$iconSpacing};
				`;
			case "bottom-left":
				return `
					bottom: ${$iconSpacing};
					left: ${$iconSpacing};
				`;
			case "top-left":
				return `
					top: ${$iconSpacing};
					left: ${$iconSpacing};
				`;
		}
	}}
`;

const MobileButtonWrapper = styled(Box)`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	margin-top: calc(1rem * 0.25);
	margin-bottom: calc(1rem * 0.25);
`;

